import { useState, useEffect, useMemo } from "react";
import {
  X,
  Plus,
  Trash2,
  Lock,
  CheckCircle2,
  Banknote,
  Landmark,
} from "lucide-react";
import {
  doc,
  getDoc,
  setDoc,
  writeBatch,
  collection,
} from "firebase/firestore";

import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../services/firebase";
import { useBuyer } from "../../contexts/BuyerContext";
import { 
  getMetalRates,
} from "../../services/metalRates";

function PayModal({ group, onClose, paymentContext = 'buyer' }) {
  const { buyer } = useBuyer();
  const { orders = [] } = group;

  const parseNum = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  };

  const round3 = (value) => parseFloat(parseNum(value).toFixed(3));
  const round2 = (value) => parseFloat(parseNum(value).toFixed(2));

  // ============================================================
  // PAYMENT METHOD DETECTION
  // ============================================================

  const hasExplicitMetal = orders.some((o) => o.paymentMethod === "Metal");

  const hasExplicitRTGS = orders.some((o) => o.paymentMethod === "RTGS");

  /*
   * If orders don't have an explicit paymentMethod,
   * the whole group is treated as RTGS.
   */
  const hasRTGS = hasExplicitRTGS || !hasExplicitMetal;
  const hasMetal = hasExplicitMetal;

  /*
   * When there is no explicit paymentMethod,
   * put ALL orders into RTGS.
   */
  const rtgsOrders = useMemo(() => {
    if (!hasExplicitRTGS && !hasExplicitMetal) {
      return orders;
    }

    return orders.filter((o) => o.paymentMethod === "RTGS");
  }, [orders, hasExplicitRTGS, hasExplicitMetal]);

  const metalOrders = useMemo(
    () => orders.filter((o) => o.paymentMethod === "Metal"),
    [orders],
  );

  // ============================================================
  // LIVE RATES
  // ============================================================

  const [liveRates, setLiveRates] = useState({
    RTGS_SALE_RATE: 0,
    RTGS_PURCHASE_RATE: 0,
    METAL_SALE_RATE: 0,
    METAL_PURCHASE_RATE: 0,
  });

  const [ratesLoading, setRatesLoading] = useState(true);

  // ============================================================
  // OTP / SETTLEMENT STATE
  // ============================================================

  const [isProcessing, setIsProcessing] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");
  // ============================================================
  // RTGS METAL ENTRIES
  // ============================================================
  const [rtgsMetalEntries, setRtgsMetalEntries] = useState([
    {
      gross: "",
      purity: "",
      billNo: "",
    },
  ]);
  // ============================================================
  // RTGS PHYSICAL CASH
  // ============================================================

  const [rtgsCashAmount, setRtgsCashAmount] = useState("");
  const [rtgsCollectionReceipt, setRtgsCollectionReceipt] = useState("");

  // ============================================================
  // RTGS BANKING
  // ============================================================

  const [rtgsBankingEntries, setRtgsBankingEntries] = useState([
    {
      amount: "",
      utr: "",
    },
  ]);

  // ============================================================
  // METAL BUCKET
  // ============================================================

  const [mtlMetalEntries, setMtlMetalEntries] = useState([
    {
      gross: "",
      purity: "",
      billNo: "",
    },
  ]);

  const [mtlCashAmount, setMtlCashAmount] = useState("");

  // ============================================================
  // FETCH LIVE RATES BASED ON PAYMENT CONTEXT
  // ============================================================

  useEffect(() => {
    let cancelled = false;

    const loadRates = async () => {
      setRatesLoading(true);

      // Determine which state to use based on payment context
      let stateToUse=buyer?.state;
      // Determine segment from orders (gold/silver/platinum)
      const firstOrder = group.orders?.[0];
      const segment = firstOrder?.segment || firstOrder?.category || 'gold';
      const normalizedSegment = segment?.toString().toLowerCase().includes('silver') ? 'silver' : 
                                segment?.toString().toLowerCase().includes('platinum') ? 'platinum' : 'gold';

      try {
        // Fetch live rates for all metals (like live rates page)
        const allMetalsRates = await getMetalRates({ 
          market: 'Your State', 
          useLiveApi: true, 
          state: stateToUse 
        });

        if (!cancelled && allMetalsRates) {
          // Extract rates for the specific segment being paid
          const segmentRates = allMetalsRates[normalizedSegment];
          
          if (segmentRates) {
            setLiveRates({
              
              // RTGS bucket: Use RTGS rates
              RTGS_SALE_RATE: segmentRates.RTGS_SALE_RATE || 0,
              RTGS_PURCHASE_RATE: segmentRates.RTGS_PURCHASE_RATE || 0,
              // Metal bucket: Use METAL rates (with state constraint applied)
              METAL_SALE_RATE: segmentRates.METAL_SALE_RATE || 0,
              METAL_PURCHASE_RATE: segmentRates.METAL_PURCHASE_RATE || 0,
              // Store base rates for reference
              INDIA_RTGS_SALE: allMetalsRates[normalizedSegment]?.india || 0,
              INDIA_RTGS_PURCHASE: allMetalsRates[normalizedSegment]?.india || 0,
              INDIA_METAL_SALE: allMetalsRates[normalizedSegment]?.india || 0,
              INDIA_METAL_PURCHASE: allMetalsRates[normalizedSegment]?.india || 0,
              // Store state and segment info
              STATE_CODE: stateToUse,
              STATE_NAME: allMetalsRates.stateName || stateToUse,
              SEGMENT: normalizedSegment,
              PAYMENT_CONTEXT: paymentContext,
              // Store all metals rates for reference
              ALL_METALS_RATES: allMetalsRates,
            });
          } else {
            // Fallback if segment rates not found
            console.warn(`[PayModal] No rates found for segment: ${normalizedSegment}`);
            setLiveRates({
              RTGS_SALE_RATE: 0,
              RTGS_PURCHASE_RATE: 0,
              METAL_SALE_RATE: 0,
              METAL_PURCHASE_RATE: 0,
              STATE_CODE: stateToUse,
              SEGMENT: normalizedSegment,
              PAYMENT_CONTEXT: paymentContext,
            });
          }
        }
      } catch (err) {
        console.error("[PayModal] Failed to fetch rates:", err);
        // Fallback to zeros
        setLiveRates({
          RTGS_SALE_RATE: 0,
          RTGS_PURCHASE_RATE: 0,
          METAL_SALE_RATE: 0,
          METAL_PURCHASE_RATE: 0,
          STATE_CODE: stateToUse,
          SEGMENT: normalizedSegment,
          PAYMENT_CONTEXT: paymentContext,
        });
      } finally {
        if (!cancelled) {
          setRatesLoading(false);
        }
      }
    };

    loadRates();

    return () => {
      cancelled = true;
    };
  }, [group, paymentContext, buyer?.state]);

  // ============================================================
  // DISPLAY TOTALS
  // ============================================================

  const sumDisplay = (arr) =>
    arr.reduce(
      (acc, order) => ({
        fineWeight: round3(
          acc.fineWeight +
            parseNum(order.displayFineWt ?? order.fineWeight ?? 0),
        ),

        totalAmount: round2(
          acc.totalAmount +
            parseNum(
              order.totalAmount ??
                order.displayTotalAmount ??
                order.displayMc ??
                order.totalMc ??
                0,
            ),
        ),
      }),
      {
        fineWeight: 0,
        totalAmount: 0,
      },
    );

  const rtgsTotals = sumDisplay(rtgsOrders);
  const metalTotals = sumDisplay(metalOrders);

  // ============================================================
  // BASE DUES
  // ============================================================

  const rtgsMetalDue = hasRTGS ? rtgsTotals.fineWeight : 0;

  const rtgsCashDue = hasRTGS ? rtgsTotals.totalAmount : 0;

  const mtlMetalDue = metalTotals.fineWeight;
  const mtlCashDue = metalTotals.totalAmount;

  // ============================================================
  // RTGS FINE ENTERED
  // ============================================================

  const rtgsFineEntered = round3(
    rtgsMetalEntries.reduce(
      (sum, entry) =>
        sum + (parseNum(entry.gross) * parseNum(entry.purity)) / 100,
      0,
    ),
  );

  // ============================================================
  // METAL FINE ENTERED
  // ============================================================

  const mtlFineEntered = round3(
    mtlMetalEntries.reduce(
      (sum, entry) =>
        sum + (parseNum(entry.gross) * parseNum(entry.purity)) / 100,
      0,
    ),
  );

  // ============================================================
  // RTGS PHYSICAL CASH
  // ============================================================

  const rtgsPhysicalAmount = round2(parseNum(rtgsCashAmount));

  // ============================================================
  // RTGS BANKING
  // ============================================================

  const rtgsBankingTotal = round2(
    rtgsBankingEntries.reduce((sum, entry) => sum + parseNum(entry.amount), 0),
  );

  // ============================================================
  // TOTAL RTGS AMOUNT ENTERED
  //
  // Physical + Banking can BOTH be used.
  // ============================================================

  const rtgsAmountEntered = round2(rtgsPhysicalAmount + rtgsBankingTotal);

  // ============================================================
  // METAL CASH
  // ============================================================

  const mtlAmountEntered = parseNum(mtlCashAmount);

  // ============================================================
  // RTGS CALCULATION
  // ============================================================

  let rtgsRemainingFine = Math.max(0, round3(rtgsMetalDue - rtgsFineEntered));

  let rtgsSurplusFine = Math.max(0, round3(rtgsFineEntered - rtgsMetalDue));

  let rtgsRemainingMc = Math.max(0, round2(rtgsCashDue - rtgsAmountEntered));

  let rtgsSurplusAmount = Math.max(0, round2(rtgsAmountEntered - rtgsCashDue));

  let rtgsEffectiveFineDue = rtgsRemainingFine;

  let rtgsEffectiveMcDue = rtgsRemainingMc;

  let rtgsSpilloverFine = 0;
  let rtgsSpilloverAmount = 0;

  // ============================================================
  // RTGS RATE CONVERSION
  //
  // NEW RULE:
  //
  // Fine surplus -> Amount
  //       PURCHASE RATE
  //
  // Amount surplus -> Fine
  //       SALE RATE
  // ============================================================

  // ------------------------------------------------------------
  // FINE SURPLUS -> MC
  // ------------------------------------------------------------

  if (rtgsSurplusFine > 0) {
    const purchaseRate = parseNum(liveRates.RTGS_PURCHASE_RATE);

    if (purchaseRate > 0) {
      const mcReduction = rtgsSurplusFine * purchaseRate;

      if (mcReduction >= rtgsRemainingMc) {
        rtgsEffectiveMcDue = 0;

        const leftover = mcReduction - rtgsRemainingMc;

        rtgsSpilloverFine = round3(leftover / purchaseRate);
      } else {
        rtgsEffectiveMcDue = round2(rtgsRemainingMc - mcReduction);
      }
    }
  }

  // ------------------------------------------------------------
  // CASH SURPLUS -> FINE
  // ------------------------------------------------------------
  else if (rtgsSurplusAmount > 0) {
    const saleRate = parseNum(liveRates.RTGS_SALE_RATE);

    if (saleRate > 0) {
      const fineReduction = rtgsSurplusAmount / saleRate;

      if (fineReduction >= rtgsRemainingFine) {
        rtgsEffectiveFineDue = 0;

        const leftover = fineReduction - rtgsRemainingFine;

        rtgsSpilloverAmount = round2(leftover * saleRate);
      } else {
        rtgsEffectiveFineDue = round3(rtgsRemainingFine - fineReduction);
      }
    }
  }

  const isRtgsCompleted =
    !hasRTGS || (rtgsEffectiveFineDue === 0 && rtgsEffectiveMcDue === 0);

  // ============================================================
  // METAL BUCKET + RTGS SPILLOVER
  //
  // NEW RULE:
  //
  // Fine surplus -> Amount
  //       METAL_PURCHASE_RATE
  //
  // Amount surplus -> Fine
  //       METAL_SALE_RATE
  // ============================================================

  let mtlMetalAfterSpill = mtlMetalDue;

  let mtlCashAfterSpill = mtlCashDue;

  if (rtgsSpilloverFine > 0) {
    if (rtgsSpilloverFine >= mtlMetalDue) {
      mtlMetalAfterSpill = 0;

      const remainingSpillFine = rtgsSpilloverFine - mtlMetalDue;

      const purchaseRate = parseNum(liveRates.METAL_PURCHASE_RATE);

      if (purchaseRate > 0) {
        const spillMcRed = remainingSpillFine * purchaseRate;

        mtlCashAfterSpill = Math.max(0, round2(mtlCashDue - spillMcRed));
      }
    } else {
      mtlMetalAfterSpill = round3(mtlMetalDue - rtgsSpilloverFine);
    }
  } else if (rtgsSpilloverAmount > 0) {
    if (rtgsSpilloverAmount >= mtlCashDue) {
      mtlCashAfterSpill = 0;

      const remainingSpillAmt = rtgsSpilloverAmount - mtlCashDue;

      const saleRate = parseNum(liveRates.METAL_SALE_RATE);

      if (saleRate > 0) {
        const spillFineRed = remainingSpillAmt / saleRate;

        mtlMetalAfterSpill = Math.max(0, round3(mtlMetalDue - spillFineRed));
      }
    } else {
      mtlCashAfterSpill = round2(mtlCashDue - rtgsSpilloverAmount);
    }
  }

  // ============================================================
  // DIRECT METAL CALCULATION
  // ============================================================

  let mtlRemainingFine = Math.max(
    0,
    round3(mtlMetalAfterSpill - mtlFineEntered),
  );

  let mtlSurplusFine = Math.max(0, round3(mtlFineEntered - mtlMetalAfterSpill));

  let mtlRemainingMc = Math.max(
    0,
    round2(mtlCashAfterSpill - mtlAmountEntered),
  );

  let mtlSurplusAmount = Math.max(
    0,
    round2(mtlAmountEntered - mtlCashAfterSpill),
  );

  let mtlEffectiveFineDue = mtlRemainingFine;

  let mtlEffectiveMcDue = mtlRemainingMc;

  // ------------------------------------------------------------
  // FINE SURPLUS -> AMOUNT
  // USE PURCHASE RATE
  // ------------------------------------------------------------

  if (mtlSurplusFine > 0) {
    const purchaseRate = parseNum(liveRates.METAL_PURCHASE_RATE);

    if (purchaseRate > 0) {
      const mcReduction = mtlSurplusFine * purchaseRate;

      mtlEffectiveMcDue = Math.max(0, round2(mtlRemainingMc - mcReduction));
    }
  }

  // ------------------------------------------------------------
  // CASH SURPLUS -> FINE
  // USE SALE RATE
  // ------------------------------------------------------------
  else if (mtlSurplusAmount > 0) {
    const saleRate = parseNum(liveRates.METAL_SALE_RATE);

    if (saleRate > 0) {
      const fineReduction = mtlSurplusAmount / saleRate;

      mtlEffectiveFineDue = Math.max(
        0,
        round3(mtlRemainingFine - fineReduction),
      );
    }
  }

  const isMetalSettlementComplete =
    !hasMetal || (mtlEffectiveFineDue === 0 && mtlEffectiveMcDue === 0);

  const isRtgsValid = () => {
    if (!hasRTGS) return true;
    if (!isRtgsCompleted) {
      return false;
    }
    const invalidMetal = rtgsMetalEntries.some(
      (entry) => parseNum(entry.gross) > 0 && !entry.billNo.trim(),
    );

    if (invalidMetal) {
      return false;
    }

    if (rtgsPhysicalAmount > 0 && !rtgsCollectionReceipt.trim()) {
      return false;
    }
    if (rtgsBankingTotal > 0) {
      const invalidBanking = rtgsBankingEntries.some(
        (entry) => parseNum(entry.amount) > 0 && !entry.utr.trim(),
      );
      if (invalidBanking) {
        return false;
      }
    }
    return true;
  };

  const isMetalValid = () => {
    if (!hasMetal) return true;
    if (!isMetalSettlementComplete) {
      return false;
    }
    const invalidMetalEntry = mtlMetalEntries.some(
      (entry) => parseNum(entry.gross) > 0 && parseNum(entry.purity) <= 0,
    );

    return !invalidMetalEntry;
  };

  const canSubmit = isRtgsValid() && isMetalValid();

  const paidQtySum =
    group.totalItems ??
    orders.reduce((sum, order) => sum + parseNum(order.quantity || 1), 0);

  const totalFineWtDelivered = round3(rtgsFineEntered + mtlFineEntered);

  const totalAmountPaid = round2(rtgsAmountEntered + mtlAmountEntered);

  const totalAmountDue = round2(rtgsCashDue + mtlCashDue);

  const totalFineWeightDue = round3(rtgsMetalDue + mtlMetalDue);

  const addRtgsMetalEntry = () => {
    setRtgsMetalEntries((prev) => [
      ...prev,
      {
        gross: "",
        purity: "",
        billNo: "",
      },
    ]);
  };

  const removeRtgsMetalEntry = (index) => {
    setRtgsMetalEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRtgsMetalEntry = (index, field, value) => {
    let processed = value;

    if (field === "purity") {
      const number = parseFloat(value);

      if (Number.isFinite(number) && number > 100) {
        processed = "100";
      }
    }

    setRtgsMetalEntries((prev) =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...entry,
              [field]: processed,
            }
          : entry,
      ),
    );
  };

  // ============================================================
  // METAL HELPERS
  // ============================================================

  const addMtlMetalEntry = () => {
    setMtlMetalEntries((prev) => [
      ...prev,
      {
        gross: "",
        purity: "",
        billNo: "",
      },
    ]);
  };

  const removeMtlMetalEntry = (index) => {
    setMtlMetalEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMtlMetalEntry = (index, field, value) => {
    let processed = value;

    if (field === "purity") {
      const number = parseFloat(value);

      if (Number.isFinite(number) && number > 100) {
        processed = "100";
      }
    }

    setMtlMetalEntries((prev) =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...entry,
              [field]: processed,
            }
          : entry,
      ),
    );
  };

  // ============================================================
  // BANKING HELPERS
  // ============================================================

  const addBankingEntry = () => {
    setRtgsBankingEntries((prev) => [
      ...prev,
      {
        amount: "",
        utr: "",
      },
    ]);
  };
  const removeBankingEntry = (index) => {
    setRtgsBankingEntries((prev) => prev.filter((_, i) => i !== index));
  };
  const updateBankingEntry = (index, field, value) => {
    setRtgsBankingEntries((prev) =>
      prev.map((entry, i) =>
        i === index
          ? {
              ...entry,
              [field]: value,
            }
          : entry,
      ),
    );
  };
  // ============================================================
  // GENERATE OTP
  // ============================================================
  const handleVerifyJre = async (settlementPayload, settlementId) => {
    const jreId = group.orders?.[0]?.jreId;
    if (!jreId) {
      alert("No JRE assigned to this order group.");
      return;
    }
    setIsProcessing(true);
    try {
      const [jreStatusDoc, jreRegDoc] = await Promise.all([
        getDoc(doc(db, "jreStatus", jreId)),
        getDoc(doc(db, "jreregistrations", jreId)),
      ]);
      const jreStatusData = jreStatusDoc.exists() ? jreStatusDoc.data() : {};
      const jreRegData = jreRegDoc.exists() ? jreRegDoc.data() : {};
      const rawMobile = (
        jreRegData.primaryMobile ||
        jreRegData.mobile ||
        jreStatusData.primaryMobile ||
        jreStatusData.mobile ||
        ""
      )
        .toString()
        .trim();

      if (!rawMobile) {
        alert(`JRE ${jreId} has no mobile number registered.`);
        return;
      }

      const cleanDigits = rawMobile.replace(/\D/g, "").slice(-10);

      if (cleanDigits.length !== 10) {
        alert(`Invalid mobile number (${rawMobile}) for JRE ${jreId}`);
        return;
      }

      const buyerId =
        settlementPayload?.buyerId ||
        localStorage.getItem("buyerId") ||
        buyer?.buyerId ||
        "UnknownBuyer";

      const sendOtpCallable = httpsCallable(functions, "sendOTP");

      const result = await sendOtpCallable({
        mobile: cleanDigits,
        useCase: "BUYER_PAYMENT_SETTLEMENT_JRE",
        params: {
          PARTY_ID: buyerId,
          SETTLEMENT_ID: settlementId,
        },
        ...settlementPayload,
      });

      if (!result.data?.success) {
        throw new Error(result.data?.error || "Failed to send OTP");
      }

      const otpData = result.data;
      const otpId = otpData.otpId;

      // Reset OTP status in case of previous verification
      const existingOtpRef = doc(db, "otps", otpData.otpId);
      await setDoc(
        existingOtpRef,
        {
          status: "pending",
          verifiedAt: null,
        },
        { merge: true },
      );

      setOtpData({
        otpId,
        jreId,
        cleanMobile: cleanDigits,
        plainOtp: otpData?.displayOtp || null,
      });

      setOtpInput("");
      setOtpError("");
    } catch (error) {
      console.error("[PayModal] OTP generation error:", error);
      alert(`OTP generation failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // ============================================================
  // VERIFY OTP + SETTLE
  // ============================================================

  const verifyOtpAndSettle = async () => {
    if (!otpData) return;

    const cleanOtpInput = otpInput.trim();

    if (!/^\d{6}$/.test(cleanOtpInput)) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    setIsProcessing(true);
    setOtpError("");

    try {
      const verifyOtpCallable = httpsCallable(functions, "verifyOTP");

      const verifyResult = await verifyOtpCallable({
        otpId: otpData.otpId,
        otp: cleanOtpInput,
      });

      if (!verifyResult.data?.success) {
        throw new Error(verifyResult.data?.error || "OTP verification failed");
      }

      // Ensure OTP ID exists before accessing Firestore
      if (!otpData.otpId) {
        throw new Error("OTP ID is missing. Please request a new OTP.");
      }

      const otpRef = doc(db, "otps", otpData.otpId);

      const otpSnap = await getDoc(otpRef);

      if (!otpSnap.exists()) {
        setOtpError("OTP record not found. Please start again.");
        return;
      }

      const data = otpSnap.data();

      const activeSettlement = data.orders
        ? {
            buyersettlementId:
              data.settlementId || data.SETTLEMENT_ID || otpData.otpId,
            settlementTimestamp:
              data.settlementTimestamp ||
              data.createdAt?.toDate?.()?.toISOString() ||
              new Date().toISOString(),
            groupSummary: data.groupSummary || {},
            paymentDetails: data.paymentDetails || {
              totalFineWtDelivered: 0,
              totalAmountPaid: 0,
              totalAmountDue: 0,
              paidQtySum: 0,
              rtgsBucket: null,
              metalBucket: null,
              ratesUsed: null,
            },
            orders: data.orders || [],
            rawOrders: data.rawOrders || [],
          }
        : null;

      // OTP verification already handled by cloud function, no need to update status again
      // Cloud function verifyOTP already sets status: "verified"

      if (!activeSettlement) {
        setOtpError("Settlement data is missing. Please start again.");
        return;
      }

      const batch = writeBatch(db);

      const settlementId = activeSettlement.buyersettlementId;

      const settlementRef = doc(collection(db, "myorders"), settlementId);

      const currentBuyerId =
        buyer?.buyerId || localStorage.getItem("buyerId") || "UnknownBuyer";

      const updatedOrders = (activeSettlement.orders || []).map((order) => ({
        ...order,
        orderStatus: "buyerPaid",
        settlementId,
        isMetalValid: true,
      }));

      const finalPayload = {
        ...activeSettlement,
        settlementId,
        buyerId: currentBuyerId,
        otpReference: otpData.otpId,
        createdAt: activeSettlement.settlementTimestamp
          ? activeSettlement.settlementTimestamp
          : new Date().toISOString(),
        orders: updatedOrders,
        settlementStatus: "completed",
        isMetalValid: true,
      };

      batch.set(settlementRef, finalPayload);

      group.orders.forEach((order) => {
        const orderDocId = order.docId || order._docId || order.id;

        if (!orderDocId) {
          console.error("[PayModal] Missing order document ID:", order);
          return;
        }

        const orderRef = doc(db, "orderList", orderDocId);

        batch.set(
          orderRef,
          {
            orderStatus: "buyerPaid",
            paidAt: new Date(),
            buyersettlementId: activeSettlement.buyersettlementId,
            isMetalValid: true,
            buyerSettled: "completed",
          },
          {
            merge: true,
          },
        );
      });

      await batch.commit();

      alert(
        `Settlement Recorded Successfully!\n\n` +
          `Paid Qty: ${finalPayload.paymentDetails.paidQtySum} items\n` +
          `Total Fine Wt Paid: ${finalPayload.paymentDetails.totalFineWtDelivered}g\n` +
          `Total Amount Paid: ₹${finalPayload.paymentDetails.totalAmountPaid.toFixed(
            2,
          )}`,
      );

      onClose();
    } catch (error) {
      console.error("[PayModal] Settlement error:", error);

      setOtpError(`Settlement failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // ============================================================
  // CREATE SETTLEMENT PAYLOAD
  // ============================================================
  console.log(liveRates);
  const handleSettle = () => {
    if (!canSubmit) return;

    if (hasRTGS && !isRtgsCompleted) {
      alert("RTGS settlement is not complete.");
      return;
    }

    if (hasMetal && !isMetalSettlementComplete) {
      alert("Metal settlement is not complete.");
      return;
    }

    const settlementId = `settle_${new Date().getTime()}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    const settlementPayload = {
      settlementId,
      settlementTimestamp: new Date().toISOString(),

      groupSummary: {
        sellerName: group.sellerName || null,

        operatorId: group.operatorId || null,

        displayFineWeight: group.displayFineWeight ?? totalFineWeightDue,

        displayTotalMc: group.displayTotalMc ?? totalAmountDue,

        totalItems: paidQtySum,
        
        // Rate info with segment and state (like live rates page)
        stateCode: liveRates.STATE_CODE || group.state || null,
        stateName: liveRates.STATE_NAME || group.state || null,
        segment: liveRates.SEGMENT || group.orders?.[0]?.segment || 'gold',
        // Store the complete metal rates for audit
        allMetalsRates: liveRates.ALL_METALS_RATES || null,
        indiaBaseRates: {
          rtgsSale: liveRates.INDIA_RTGS_SALE || 0,
          rtgsPurchase: liveRates.INDIA_RTGS_PURCHASE || 0,
          metalSale: liveRates.INDIA_METAL_SALE || 0,
          metalPurchase: liveRates.INDIA_METAL_PURCHASE || 0,
        },
      },

      paymentDetails: {
        // ------------------------------------------------------
        // ACTUAL TOTALS
        // ------------------------------------------------------

        totalFineWtDelivered: totalFineWtDelivered,

        totalAmountPaid: totalAmountPaid,

        totalAmountDue: totalAmountDue,

        totalFineWeightDue: totalFineWeightDue,

        paidQtySum: paidQtySum,

        // ------------------------------------------------------
        // RTGS BUCKET
        // ------------------------------------------------------

        rtgsBucket: hasRTGS
          ? {
              dueFineWt: rtgsMetalDue,

              dueAmount: rtgsCashDue,

              metalEntries: rtgsMetalEntries
                .filter((entry) => parseNum(entry.gross) > 0)
                .map((entry) => ({
                  grossWt: parseNum(entry.gross),

                  purity: parseNum(entry.purity),

                  fineWt: round3(
                    (parseNum(entry.gross) * parseNum(entry.purity)) / 100,
                  ),

                  billNo: entry.billNo.trim(),
                })),

              calculatedFineWt: rtgsFineEntered,

              // ------------------------------------------------
              // PHYSICAL CASH
              // ------------------------------------------------

              physicalCash: {
                amount: rtgsPhysicalAmount,

                collectionReceiptNo: rtgsCollectionReceipt.trim(),
              },

              // ------------------------------------------------
              // BANKING
              // ------------------------------------------------

              banking: {
                totalAmount: rtgsBankingTotal,

                entries: rtgsBankingEntries
                  .filter(
                    (entry) => parseNum(entry.amount) > 0 || entry.utr.trim(),
                  )
                  .map((entry) => ({
                    amount: parseNum(entry.amount),

                    utr: entry.utr.trim(),
                  })),
              },

              // ------------------------------------------------
              // COMBINED AMOUNT
              // ------------------------------------------------

              totalAmountEntered: rtgsAmountEntered,

              paymentMode:
                rtgsPhysicalAmount > 0 && rtgsBankingTotal > 0
                  ? "physical_and_banking"
                  : rtgsPhysicalAmount > 0
                  ? "physical"
                  : rtgsBankingTotal > 0
                  ? "banking"
                  : "none",

              // ------------------------------------------------
              // SPILLOVER
              // ------------------------------------------------

              spilloverToMetal: {
                fineWt: rtgsSpilloverFine,

                amount: rtgsSpilloverAmount,
              },

              effectiveFineDue: rtgsEffectiveFineDue,

              effectiveMcDue: rtgsEffectiveMcDue,

              settlementComplete: isRtgsCompleted,
            }
          : null,

        // ------------------------------------------------------
        // METAL BUCKET
        // ------------------------------------------------------

        metalBucket: hasMetal
          ? {
              dueFineWt: mtlMetalDue,

              dueAmount: mtlCashDue,

              metalEntries: mtlMetalEntries
                .filter((entry) => parseNum(entry.gross) > 0)
                .map((entry) => ({
                  grossWt: parseNum(entry.gross),

                  purity: parseNum(entry.purity),

                  fineWt: round3(
                    (parseNum(entry.gross) * parseNum(entry.purity)) / 100,
                  ),

                  billNo: entry.billNo?.trim() || null,
                })),

              calculatedFineWt: mtlFineEntered,

              amountEntered: mtlAmountEntered,

              effectiveFineDue: mtlEffectiveFineDue,

              effectiveMcDue: mtlEffectiveMcDue,

              settlementComplete: isMetalSettlementComplete,
            }
          : null,

        // ------------------------------------------------------
        // RATES SNAPSHOT
        // ------------------------------------------------------

        ratesUsed: liveRates,
      },

      // ========================================================
      // ORDERS
      // ========================================================

      orders: orders.map((order) => ({
        ...order,

        orderStatus: "buyerPaid",

        isMetalValid: true,

        displayFineWt: order.displayFineWt ?? order.fineWeight ?? 0,

        displayMc: order.displayMc ?? order.totalMc ?? 0,

        displayTotalAmount:
          order.totalAmount ??
          order.displayTotalAmount ??
          order.displayMc ??
          order.totalMc ??
          0,
      })),

      rawOrders: orders,
    };

    console.log("========== SETTLEMENT PAYLOAD ==========");

    console.log(JSON.stringify(settlementPayload, null, 2));

    handleVerifyJre(settlementPayload, settlementId);
  };

  // ============================================================
  // OTP SCREEN
  // ============================================================

  if (otpData) {
    return (
      <div className="od-modal-overlay" onClick={onClose}>
        <div className="od-modal" onClick={(e) => e.stopPropagation()}>
          <div className="od-modal-header">
            <span className="od-modal-title">Verify Settlement with JRE</span>

            <button
              className="od-modal-close"
              onClick={onClose}
              disabled={isProcessing}
            >
              <X size={20} />
            </button>
          </div>

          <div
            className="od-modal-body"
            style={{
              textAlign: "center",
              padding: "2rem",
            }}
          >
            <h3>Enter OTP</h3>

            <p
              style={{
                color: "#666",
                marginBottom: "0.5rem",
              }}
            >
              An OTP has been sent to the JRE's mobile (+91****
              {otpData.cleanMobile?.slice(-4)}
              ). Please ask them for it.
            </p>

            {otpData.plainOtp && (
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#166534",
                  marginBottom: "1rem",
                  display: "inline-block",
                }}
              >
                </div>
            )}

            <input
              type="text"
              maxLength={6}
              value={otpInput}
              onChange={(e) => {
                setOtpInput(e.target.value.replace(/\D/g, ""));

                setOtpError("");
              }}
              placeholder="000000"
              className="od-input"
              style={{
                fontSize: "2rem",
                textAlign: "center",
                letterSpacing: "0.5rem",
                padding: "1rem",
                width: "200px",
                margin: "0 auto",
                display: "block",
              }}
              disabled={isProcessing}
            />

            {otpError && (
              <p
                style={{
                  color: "red",
                  marginTop: "0.5rem",
                }}
              >
                {otpError}
              </p>
            )}
          </div>

          <div className="od-modal-footer">
            <button
              className="od-btn-cancel"
              onClick={() => {
                setOtpData(null);
              }}
              disabled={isProcessing}
            >
              Back
            </button>

            <button
              className="od-btn-settle"
              onClick={verifyOtpAndSettle}
              disabled={isProcessing || otpInput.length !== 6}
            >
              {isProcessing ? "Verifying..." : "Verify & Complete Settlement"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // MAIN MODAL
  // ============================================================

  return (
    <div className="od-modal-overlay" onClick={onClose}>
      <div className="od-modal" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}

        <div className="od-modal-header">
          <span className="od-modal-title">Settle Payment</span>

          <button className="od-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* ======================================================
            DUE SUMMARY
        ====================================================== */}

        <div className="od-modal-due-summary">
          <span className="od-due-label">To Be Paid</span>

          <div className="od-due-values">
            <span className="od-due-item">
              Fine Wt: <strong>{totalFineWeightDue.toFixed(3)}g</strong>
            </span>

            <span className="od-due-sep">·</span>

            <span className="od-due-item">
              Amount: <strong>₹{totalAmountDue.toFixed(2)}</strong>
            </span>
          </div>
        </div>

        <div className="od-modal-body">
          {/* ==================================================
              RTGS BUCKET
          ================================================== */}

          {hasRTGS && (
            <section className="od-bucket">
              <div className="od-bucket-header">
                <h4 className="od-bucket-title">RTGS Bucket</h4>

                {isRtgsCompleted && (
                  <span className="od-completed-badge">
                    <CheckCircle2 size={14} />
                    Completed
                  </span>
                )}
              </div>

              <div className="od-bucket-rates">
                <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                  <strong>{liveRates.SEGMENT?.toUpperCase() || 'GOLD'}</strong> · {liveRates.STATE_NAME || liveRates.STATE_CODE || 'State'}
                </div>
                <span className="od-rate od-rate--sale">
                  Sale ₹{liveRates.RTGS_SALE_RATE}
                  /g
                </span>

                <span className="od-rate od-rate--purchase">
                  Purchase ₹{liveRates.RTGS_PURCHASE_RATE}
                  /g
                </span>
              </div>

              <div className="od-bucket-dues">
                <span>
                  Fine Wt Due:{" "}
                  <strong>{rtgsEffectiveFineDue.toFixed(3)}g</strong>
                </span>

                <span>
                  Amount Due: <strong>₹{rtgsEffectiveMcDue.toFixed(2)}</strong>
                </span>
              </div>

              <div className="od-section-subtitle">Fine Wt Details</div>

              <div className="od-multi-entries">
                {rtgsMetalEntries.map((entry, index) => (
                  <div key={index} className="od-banking-row">
                    <div className="od-input-row flex-1">
                      <label>Gross (g)</label>

                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={entry.gross}
                        onChange={(e) =>
                          updateRtgsMetalEntry(index, "gross", e.target.value)
                        }
                        placeholder="0.000"
                        className="od-input"
                      />
                    </div>

                    <div className="od-input-row flex-1">
                      <label>Purity (%)</label>

                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={entry.purity}
                        onChange={(e) =>
                          updateRtgsMetalEntry(index, "purity", e.target.value)
                        }
                        placeholder="99.50"
                        className="od-input"
                      />
                    </div>

                    <div className="od-input-row flex-1">
                      <label>
                        Bill No <span className="od-req">*</span>
                      </label>

                      <input
                        type="text"
                        value={entry.billNo}
                        onChange={(e) =>
                          updateRtgsMetalEntry(index, "billNo", e.target.value)
                        }
                        placeholder="Bill #"
                        className="od-input"
                      />
                    </div>

                    {rtgsMetalEntries.length > 1 && (
                      <button
                        type="button"
                        className="od-btn-remove-utr"
                        onClick={() => removeRtgsMetalEntry(index)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  className="od-btn-add-utr"
                  onClick={addRtgsMetalEntry}
                >
                  <Plus size={14} />
                  Add Metal Entry
                </button>
              </div>

              <div className="od-calc-output">
                Calculated Fine Wt:{" "}
                <strong>{rtgsFineEntered.toFixed(3)}g</strong>
              </div>

              {/* ==================================================
                  AMOUNT DETAILS
              ================================================== */}

              <div className="od-section-subtitle">Amount Details</div>

              {/* ==================================================
                  PHYSICAL CASH
              ================================================== */}

              <div
                className="od-cash-method"
                style={{
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "10px",
                    fontWeight: "600",
                    color: "var(--brand-primary)",
                  }}
                >
                  <Banknote size={16} />
                  <span>Physical Cash</span>
                </div>

                <div className="od-input-grid-2">
                  <div className="od-input-row">
                    <label>Amount (₹)</label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={rtgsCashAmount}
                      onChange={(e) => setRtgsCashAmount(e.target.value)}
                      placeholder="₹0.00"
                      className="od-input"
                    />
                  </div>

                  <div className="od-input-row">
                    <label>
                      Collection Receipt No <span className="od-req">*</span>
                    </label>

                    <input
                      type="text"
                      value={rtgsCollectionReceipt}
                      onChange={(e) => setRtgsCollectionReceipt(e.target.value)}
                      placeholder="Receipt No"
                      className="od-input"
                    />
                  </div>
                </div>
              </div>

              {/* ==================================================
                  BANKING
              ================================================== */}

              <div className="od-cash-method">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "10px",
                    fontWeight: "600",
                    color: "var(--brand-primary)",
                  }}
                >
                  <Landmark size={16} />
                  <span>Banking</span>
                </div>

                <div className="od-banking-entries">
                  {rtgsBankingEntries.map((entry, index) => (
                    <div key={index} className="od-banking-row">
                      <div className="od-input-row flex-1">
                        <label>Amount (₹)</label>

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={entry.amount}
                          onChange={(e) =>
                            updateBankingEntry(index, "amount", e.target.value)
                          }
                          placeholder="₹0.00"
                          className="od-input"
                        />
                      </div>

                      <div className="od-input-row flex-1">
                        <label>
                          UTR Number <span className="od-req">*</span>
                        </label>

                        <input
                          type="text"
                          value={entry.utr}
                          onChange={(e) =>
                            updateBankingEntry(index, "utr", e.target.value)
                          }
                          placeholder="UTR Number"
                          className="od-input"
                        />
                      </div>

                      {rtgsBankingEntries.length > 1 && (
                        <button
                          type="button"
                          className="od-btn-remove-utr"
                          onClick={() => removeBankingEntry(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    className="od-btn-add-utr"
                    onClick={addBankingEntry}
                  >
                    <Plus size={14} />
                    Add UTR Entry
                  </button>
                </div>
              </div>

              {/* ==================================================
                  COMBINED RTGS TOTAL
              ================================================== */}

              <div
                className="od-calc-output"
                style={{
                  marginTop: "12px",
                }}
              >
                RTGS Amount Paid:{" "}
                <strong>₹{rtgsAmountEntered.toFixed(2)}</strong>
                {rtgsPhysicalAmount > 0 && rtgsBankingTotal > 0 && (
                  <span
                    style={{
                      display: "block",
                      marginTop: "4px",
                      fontSize: "12px",
                      opacity: 0.75,
                    }}
                  >
                    Cash ₹{rtgsPhysicalAmount.toFixed(2)} + Banking ₹
                    {rtgsBankingTotal.toFixed(2)}
                  </span>
                )}
              </div>
            </section>
          )}

          {/* ==================================================
              METAL BUCKET
          ================================================== */}

          {hasMetal && (
            <section
              className={`od-bucket ${
                hasRTGS && !isRtgsCompleted ? "od-bucket--locked" : ""
              }`}
            >
              <div className="od-bucket-header">
                <h4 className="od-bucket-title">Metal Bucket</h4>

                {hasRTGS && !isRtgsCompleted && (
                  <span className="od-locked-badge">
                    <Lock size={12} />
                    Locked
                  </span>
                )}

                {isMetalSettlementComplete && (!hasRTGS || isRtgsCompleted) && (
                  <span className="od-completed-badge">
                    <CheckCircle2 size={14} />
                    Completed
                  </span>
                )}
              </div>

              <div className="od-bucket-rates">
                <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                  <strong>METAL</strong> · {liveRates.STATE_NAME || liveRates.STATE_CODE || 'State'}
                </div>
                <span className="od-rate od-rate--sale">
                  Sale ₹{liveRates.METAL_SALE_RATE}
                  /g
                </span>

                <span className="od-rate od-rate--purchase">
                  Purchase ₹{liveRates.METAL_PURCHASE_RATE}
                  /g
                </span>
              </div>

              <div className="od-bucket-dues">
                <span>
                  Fine Wt Due:{" "}
                  <strong>{mtlEffectiveFineDue.toFixed(3)}g</strong>
                </span>

                <span>
                  Amount Due: <strong>₹{mtlEffectiveMcDue.toFixed(2)}</strong>
                </span>
              </div>

              <div className="od-section-subtitle">Fine Wt Details</div>

              <div className="od-multi-entries">
                {mtlMetalEntries.map((entry, index) => (
                  <div key={index} className="od-banking-row">
                    <div className="od-input-row flex-1">
                      <label>Gross (g)</label>

                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        value={entry.gross}
                        onChange={(e) =>
                          updateMtlMetalEntry(index, "gross", e.target.value)
                        }
                        placeholder="0.000"
                        className="od-input"
                        disabled={hasRTGS && !isRtgsCompleted}
                      />
                    </div>

                    <div className="od-input-row flex-1">
                      <label>Purity (%)</label>

                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={entry.purity}
                        onChange={(e) =>
                          updateMtlMetalEntry(index, "purity", e.target.value)
                        }
                        placeholder="99.50"
                        className="od-input"
                        disabled={hasRTGS && !isRtgsCompleted}
                      />
                    </div>

                    <div className="od-input-row flex-1">
                      <label>
                        Bill No <span className="od-req">*</span>
                      </label>

                      <input
                        type="text"
                        value={entry.billNo}
                        onChange={(e) =>
                          updateMtlMetalEntry(index, "billNo", e.target.value)
                        }
                        placeholder="Bill #"
                        className="od-input"
                        disabled={hasRTGS && !isRtgsCompleted}
                      />
                    </div>

                    {mtlMetalEntries.length > 1 && (
                      <button
                        type="button"
                        className="od-btn-remove-utr"
                        onClick={() => removeMtlMetalEntry(index)}
                        disabled={hasRTGS && !isRtgsCompleted}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  className="od-btn-add-utr"
                  onClick={addMtlMetalEntry}
                  disabled={hasRTGS && !isRtgsCompleted}
                >
                  <Plus size={14} />
                  Add Metal Entry
                </button>
              </div>

              <div className="od-calc-output">
                Calculated Fine Wt:{" "}
                <strong>{mtlFineEntered.toFixed(3)}g</strong>
              </div>

              <div className="od-section-subtitle">Amount Details</div>

              <div className="od-input-row">
                <label>Amount (₹)</label>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={mtlCashAmount}
                  onChange={(e) => setMtlCashAmount(e.target.value)}
                  placeholder="₹0.00"
                  className="od-input"
                  disabled={hasRTGS && !isRtgsCompleted}
                />
              </div>
            </section>
          )}

          {/* ==================================================
              TOTALS
          ================================================== */}

          <section className="od-totals">
            <div className="od-total-row">
              <span>Paid Qty Sum</span>

              <strong>{paidQtySum} items</strong>
            </div>

            <div className="od-total-row">
              <span>Total Fine Wt Due</span>

              <strong>{totalFineWeightDue.toFixed(3)}g</strong>
            </div>

            <div className="od-total-row">
              <span>Total Amount Due</span>

              <strong>₹{totalAmountDue.toFixed(2)}</strong>
            </div>

            <div className="od-total-row">
              <span>Total Fine Wt Delivered</span>

              <strong>{totalFineWtDelivered.toFixed(3)}g</strong>
            </div>

            <div className="od-total-row">
              <span>Total Amount Paid</span>

              <strong>₹{totalAmountPaid.toFixed(2)}</strong>
            </div>
          </section>

          {/* ==================================================
              VALIDATION
          ================================================== */}

          {hasMetal && !isMetalSettlementComplete && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "6px",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                color: "#9a3412",
                fontSize: "13px",
              }}
            >
              Metal settlement pending: {mtlEffectiveFineDue.toFixed(3)}g fine
              weight and ₹{mtlEffectiveMcDue.toFixed(2)} amount remaining.
            </div>
          )}

          {isMetalSettlementComplete && (!hasRTGS || isRtgsCompleted) && (
            <div
              style={{
                marginTop: "12px",
                padding: "10px 12px",
                borderRadius: "6px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                color: "#166534",
                fontSize: "13px",
              }}
            >
              <CheckCircle2
                size={14}
                style={{
                  verticalAlign: "middle",
                  marginRight: "5px",
                }}
              />
              Complete settlement amount received.
            </div>
          )}

          <p className="od-rates-note">
            {ratesLoading
              ? "Loading live rates…"
              : `Live Rates Applied — Segment: ${liveRates.SEGMENT?.toUpperCase() || 'GOLD'} · State: ${liveRates.STATE_NAME || liveRates.STATE_CODE || 'DL'} · RTGS Sale: ₹${liveRates.RTGS_SALE_RATE}/g · Purchase: ₹${liveRates.RTGS_PURCHASE_RATE}/g · Metal Sale: ₹${liveRates.METAL_SALE_RATE}/g · Purchase: ₹${liveRates.METAL_PURCHASE_RATE}/g`}
          </p>
        </div>

        {/* FOOTER */}

        <div className="od-modal-footer">
          <button
            className="od-btn-cancel"
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>

          <button
            className="od-btn-settle"
            onClick={handleSettle}
            disabled={!canSubmit || isProcessing}
          >
            {isProcessing ? "Processing..." : "Confirm Settlement"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PayModal;
